import { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { LoadingSpinner, ErrorBoundary } from '@elka/ui';
import ProtectedRoute from '@/components/ProtectedRoute';
import WorkerLayout from '@/components/layout/WorkerLayout';
import PhoneLoginForm from '@/features/auth/components/PhoneLoginForm';
import { lazyWithReloadOnError } from '@/lib/lazyWithReloadOnError';

const DashboardPage = lazyWithReloadOnError(() => import('@/pages/DashboardPage'));
const SchedulePage = lazyWithReloadOnError(() => import('@/pages/SchedulePage'));
const TimeReportPage = lazyWithReloadOnError(() => import('@/pages/TimeReportPage'));
const DailyReportPage = lazyWithReloadOnError(() => import('@/pages/DailyReportPage'));
const PayslipsPage = lazyWithReloadOnError(() => import('@/pages/PayslipsPage'));
const VacationPage = lazyWithReloadOnError(() => import('@/pages/VacationPage'));
const PurchaseRequestsPage = lazyWithReloadOnError(() => import('@/pages/PurchaseRequestsPage'));
const PettyCashPage = lazyWithReloadOnError(() => import('@/pages/PettyCashPage'));
const NotificationsPage = lazyWithReloadOnError(() => import('@/pages/NotificationsPage'));
const InventoryScanPage = lazyWithReloadOnError(() => import('@/pages/InventoryScanPage'));
const InventoryPickupConfirmPage = lazyWithReloadOnError(
  () => import('@/pages/InventoryPickupConfirmPage')
);
const InventoryPickupDonePage = lazyWithReloadOnError(
  () => import('@/pages/InventoryPickupDonePage')
);
const InventoryReturnPage = lazyWithReloadOnError(() => import('@/pages/InventoryReturnPage'));
const InventoryHistoryPage = lazyWithReloadOnError(() => import('@/pages/InventoryHistoryPage'));
const InventoryItemsPage = lazyWithReloadOnError(() => import('@/pages/InventoryItemsPage'));
const InventoryItemPage = lazyWithReloadOnError(() => import('@/pages/InventoryItemPage'));

function PageLoader() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <LoadingSpinner size="md" centered={false} />
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<PhoneLoginForm />} />

          {/* Pathless layout route — WorkerLayout renders <Outlet /> for children */}
          <Route
            element={
              <ProtectedRoute>
                <WorkerLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route
              path="/dashboard"
              element={
                <Suspense fallback={<PageLoader />}>
                  <DashboardPage />
                </Suspense>
              }
            />
            <Route
              path="/schedule"
              element={
                <Suspense fallback={<PageLoader />}>
                  <SchedulePage />
                </Suspense>
              }
            />

            {/* Reports sub-routes */}
            <Route
              path="/reports/time"
              element={
                <Suspense fallback={<PageLoader />}>
                  <TimeReportPage />
                </Suspense>
              }
            />
            <Route
              path="/reports/daily"
              element={
                <Suspense fallback={<PageLoader />}>
                  <DailyReportPage />
                </Suspense>
              }
            />
            <Route path="/reports" element={<Navigate to="/reports/time" replace />} />

            {/* Personal sub-routes */}
            <Route
              path="/personal/payslips"
              element={
                <Suspense fallback={<PageLoader />}>
                  <PayslipsPage />
                </Suspense>
              }
            />
            <Route
              path="/personal/vacation"
              element={
                <Suspense fallback={<PageLoader />}>
                  <VacationPage />
                </Suspense>
              }
            />
            <Route
              path="/personal/purchase-requests"
              element={
                <Suspense fallback={<PageLoader />}>
                  <PurchaseRequestsPage />
                </Suspense>
              }
            />
            <Route
              path="/personal/petty-cash"
              element={
                <Suspense fallback={<PageLoader />}>
                  <PettyCashPage />
                </Suspense>
              }
            />
            <Route path="/personal" element={<Navigate to="/personal/payslips" replace />} />

            <Route
              path="/notifications"
              element={
                <Suspense fallback={<PageLoader />}>
                  <NotificationsPage />
                </Suspense>
              }
            />

            {/* Inventory */}
            <Route
              path="/inventory/scan"
              element={
                <Suspense fallback={<PageLoader />}>
                  <InventoryScanPage />
                </Suspense>
              }
            />
            <Route
              path="/inventory/pickup/confirm"
              element={
                <Suspense fallback={<PageLoader />}>
                  <InventoryPickupConfirmPage />
                </Suspense>
              }
            />
            <Route
              path="/inventory/pickup/done"
              element={
                <Suspense fallback={<PageLoader />}>
                  <InventoryPickupDonePage />
                </Suspense>
              }
            />
            <Route
              path="/inventory/return"
              element={
                <Suspense fallback={<PageLoader />}>
                  <InventoryReturnPage />
                </Suspense>
              }
            />
            <Route
              path="/inventory/history"
              element={
                <Suspense fallback={<PageLoader />}>
                  <InventoryHistoryPage />
                </Suspense>
              }
            />
            <Route
              path="/inventory/items"
              element={
                <Suspense fallback={<PageLoader />}>
                  <InventoryItemsPage />
                </Suspense>
              }
            />
            <Route
              path="/inventory/items/:itemId"
              element={
                <Suspense fallback={<PageLoader />}>
                  <InventoryItemPage />
                </Suspense>
              }
            />
            <Route path="/inventory" element={<Navigate to="/inventory/scan" replace />} />

            {/* Redirect old routes */}
            <Route path="/daily-report" element={<Navigate to="/reports/daily" replace />} />
            <Route path="/time-report" element={<Navigate to="/reports/time" replace />} />
            <Route path="/payslips" element={<Navigate to="/personal/payslips" replace />} />
            <Route path="/vacation" element={<Navigate to="/personal/vacation" replace />} />
          </Route>

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
